  import React, {
    forwardRef,
    useImperativeHandle,
    useState,
  } from "react";
  import * as Popover from "@radix-ui/react-popover";
  import { ClipboardCheck, Copy } from "lucide-react";

  export interface EntityPopoverItem {
    content: React.ReactNode;
    copyValue?: string;
  }

  export interface EntityPopoverHandle {
    open: () => void;
    close: () => void;
    toggle: () => void;
  }

  export interface EntityPopoverProps {
    icon?: React.ReactNode;
    title?: string;
    allowCopy?: boolean;
    items: string[] | EntityPopoverItem[];
  }

  export const EntityPopover = forwardRef<EntityPopoverHandle, EntityPopoverProps>(
    (
      {
        icon,
        title,
        allowCopy = false,
        items,
      },
      ref
    ) => {
      const [open, setOpen] = useState(false);

      const [copiedIndex, setCopiedIndex] =
        useState<number | null>(null);

      useImperativeHandle(ref, () => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
        toggle: () =>
          setOpen(prev => !prev),
      }));

      const handleCopy = async (text: string, index: number) => {
        try {
          await navigator.clipboard.writeText(
            text
          );

          setCopiedIndex(index);

          setTimeout(() => {
            setCopiedIndex(null);
          }, 1000);

        } catch (error) {
          console.error( "Copy failed",error);
        }
      };

      const isStringList =items.length === 0 || typeof items[0] === "string";

      const renderCopyButton = (value: string,index: number) => (
        <button
          onClick={() =>
            handleCopy(value, index)
          }
          className="shrink-0 rounded-[6px] p-[2px] focus:outline-none"
        >
          {copiedIndex === index ? (
            <ClipboardCheck
              size={16}
              strokeWidth={1.75}
              className="text-green-500"
            />
          ) : (
            <Copy
              size={16}
              strokeWidth={1.75}
              className="text-[#737373]"
            />
          )}
        </button>
      );

      return (
        <Popover.Root
          open={open}
          onOpenChange={setOpen}
        >
          <Popover.Anchor />

          <Popover.Content
            className="z-50 focus:outline-none"
            side="bottom"
            align="center"
            sideOffset={0}
            avoidCollisions
            collisionPadding={16}

          >
              <div className ="min-w-[280px] max-w-[600px] rounded-[8px] border border-[#E5E5E5] bg-white p-1 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.10),0_2px_4px_-2px_rgba(0,0,0,0.10)]">
              {(icon || title) && (
                <div className="sticky top-0 z-10 flex items-center gap-1 bg-white px-2 py-[6px]">
                  {icon}

                  {title && (
                    <span className="text-xs font-medium leading-4 text-[#94A3B8]"
                    >
                      {title}
                    </span>
                  )}
                </div>
              )}

              <div className="max-h-[228px] overflow-y-auto pr-[2px]"
              >
                {isStringList ? (
                  <div >
                    {(items as string[]).map(
                      (item, index) => (
                        <div
                          key={`${item}-${index}`}
                          className="flex items-center justify-between gap-2 rounded-[6px] px-2 py-[6px]"
                        >
                          <span
                            className="max-w-[270px] truncate text-sm font-normal leading-5 text-[#0A0A0A]"
                            title={item}
                          >
                            {item}
                          </span>

                          {allowCopy && (
                            renderCopyButton(item, index)
                          )}
                        </div>
                      )
                    )}
                  </div>
                ) : (
                <div>
                  {(
                    items as EntityPopoverItem[]
                  ).map(
                    (item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between gap-2 pr-2"
                      >
                        <div className="flex-1">
                          {item.content}
                        </div>

                        {item.copyValue && (
                          renderCopyButton(item.copyValue, index)
                        )}
                      </div>
                    )
                  )}
                </div>
                )}
              </div>
            </div>
          </Popover.Content>
        </Popover.Root>
      );
    }
  );
